import type { Components } from 'react-markdown';

import { ExternalLink } from '../external-link';
import classes from './markdown-prose.module.css';

/**
 * ### markdown
 *
 * @description
 * The behavioural half of markdown rendering. Everything presentational is now Mantine
 * `Typography`'s job (see `MarkdownProse`); this map keeps only overrides that earn their place —
 * two behavioural, one structural:
 *
 * - `a` always leaves the app. Every consumer renders model-written markdown, and a model only
 *   ever links out — so a link should open in a new tab and not navigate the reader off their
 *   conversation. A markdown link can parse without an href; that is text, not a link.
 * - Headings shift down one level. A chat answer lives inside a page that already owns the page
 *   `<h1>`, so a model's `#` must not emit a second one: every level drops by one and `h6` floors.
 * - `table` gets a scroll container. Typography styles the `<table>` itself, but a table cannot
 *   scroll horizontally without an ancestor to scroll inside — so this wraps it in one. The entry
 *   looks presentational, but it is there for containment (a wide table must not drag the chat
 *   region sideways), not looks. `td`/`th` stay unmapped so react-markdown keeps converting GFM's
 *   `align` attribute to an inline style; mapping them would silently drop column alignment.
 */
const markdown: Partial<Components> = {
  a: ({ node: _node, children, ref: _r, href, ...props }) =>
    href ? (
      <ExternalLink {...props} href={href}>
        {children}
      </ExternalLink>
    ) : (
      <>{children}</>
    ),
  h1: ({ node: _node, ref: _r, children, ...props }) => <h2 {...props}>{children}</h2>,
  h2: ({ node: _node, ref: _r, children, ...props }) => <h3 {...props}>{children}</h3>,
  h3: ({ node: _node, ref: _r, children, ...props }) => <h4 {...props}>{children}</h4>,
  h4: ({ node: _node, ref: _r, children, ...props }) => <h5 {...props}>{children}</h5>,
  h5: ({ node: _node, ref: _r, children, ...props }) => <h6 {...props}>{children}</h6>,
  h6: ({ node: _node, ref: _r, children, ...props }) => <h6 {...props}>{children}</h6>,
  table: ({ node: _node, children, ref: _r, ...props }) => (
    <div className={classes.tableScroll}>
      <table {...props}>{children}</table>
    </div>
  ),
};

export default markdown;
